import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';

import usePublicApi from 'public-api';
import appRoutes from 'routes/codegen/appRoutes';
import authRoutes from 'routes/codegen/authRoutes';
import { useAsyncFunction } from 'stores/hooks';
import AuthCard from 'ui-brand/components/AuthCard';
import Button from 'ui-brand/components/Button';
import Loader from 'ui-brand/components/Loader';
import ProductSelection from 'ui-brand/components/ProductSelection';

import ErrorStep from './components/ErrorStep';
import SignInLink from './components/SignInLink';
import PlatformIntegrationForm from './PlatformIntegrationForm';
import LegalLinks from './signup/LegalLinks';
import PlatformIntegrationStore from './stores/PlatformIntegrationStore';

function PlatformIntegration() {
  const { platformIntegrationId } = useParams<{ platformIntegrationId: UUIDField }>();
  const publicApi = usePublicApi();
  const [store] = useState(() => new PlatformIntegrationStore(publicApi, platformIntegrationId!));
  const [handleSubmit, isSubmitting] = useAsyncFunction(store.submit);

  useEffect(() => {
    store.load();
  }, [store]);

  if (store.loadFailed) return <ErrorStep onRetry={store.load} />;
  if (!store.platformIntegration) {
    return (
      <div className="flex justify-center py-32">
        <Loader size="lg" />
      </div>
    );
  }

  const connectAccountUrl = appRoutes.onboardingPlatformIntegrationPlatformIntegrationIdConnectAccount({
    platformIntegrationId: store.platformIntegrationId,
  });
  const loginUrl = `${authRoutes.authLogin({ lang: window.appState.lang })}?next=${connectAccountUrl}`;
  const shopName = store.platformIntegration.prefill.shop_name;

  return (
    <AuthCard
      eyebrow={interpolate(gettext('%(platform)s add-on installed'), { platform: store.platformName }, true)}
      title={gettext('Finish setting up your Retino account')}
      subtitle={
        shopName
          ? interpolate(gettext('We will connect it with your store %(shop)s.'), { shop: shopName }, true)
          : undefined
      }
    >
      <form
        className="mt-8"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <fieldset disabled={isSubmitting} className="flex min-w-0 flex-col gap-5">
          <PlatformIntegrationForm store={store} />
        </fieldset>

        <h2 className="mt-12 text-center text-xl font-semibold text-retino-darkviolet">
          {gettext('What can we help you with?')}
        </h2>
        <div className="mt-4">
          <ProductSelection
            stacked
            disabled={isSubmitting}
            value={store.data.selected_products}
            onChange={(value) => store.updateData({ selected_products: value })}
          />
        </div>
        {store.errors.other && <p className="mt-4 text-center text-[13px] text-red-600">{store.errors.other}</p>}

        <div className="mt-8 text-center">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            arrow
            disabled={store.data.selected_products.length === 0}
            loading={isSubmitting}
          >
            {gettext('Create account')}
          </Button>
          <LegalLinks />
          <SignInLink href={loginUrl} />
        </div>
      </form>
    </AuthCard>
  );
}

export default observer(PlatformIntegration);
